import React, { useState, useEffect } from 'react';
import { Bell, X, Check, AlertCircle, Info, CheckCircle, MessageSquare, Gavel, Clock, Trophy, Calendar, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '@/services/notificationService';
import { Notification } from '@/types/navigation';
import { formatDistanceToNow } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onNotificationsChange?: () => void;
}

const getNotificationIcon = (type: Notification['type'], iconName?: string) => {
  switch (iconName || type) {
    case 'MessageSquare': return <MessageSquare className="h-5 w-5 text-white" />;
    case 'Gavel': return <Gavel className="h-5 w-5 text-white" />;
    case 'Clock': return <Clock className="h-5 w-5 text-white" />;
    case 'Trophy': return <Trophy className="h-5 w-5 text-white" />;
    case 'Calendar': return <Calendar className="h-5 w-5 text-white" />;
    case 'Users': return <Users className="h-5 w-5 text-white" />;
    default: return <Info className="h-5 w-5 text-white" />;
  }
};

const getPriorityColor = (priority: Notification['priority']) => {
  switch (priority) {
    case 'urgent': return 'border-l-red-500 bg-red-500/10';
    case 'high': return 'border-l-orange-500 bg-orange-500/10';
    case 'medium': return 'border-l-emerald-500 bg-emerald-500/10';
    case 'low': return 'border-l-blue-500 bg-blue-500/10';
    default: return 'border-l-gray-500 bg-white/5';
  }
};

export default function NotificationCenter({ isOpen, onClose, onNotificationsChange }: NotificationCenterProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
      if (user) {
        notificationService.subscribeToNotifications(
          user.id,
          isAdmin ? 'admin' : 'user',
          setNotifications
        );
      }
    }
    return () => { if (user) notificationService.unsubscribeFromNotifications(user.id); };
  }, [isOpen, user, isAdmin]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      let fetchedNotifications: Notification[] = [];
      if (user) {
        fetchedNotifications = await notificationService.getUserNotifications(user.id, isAdmin ? 'admin' : 'user');
      } else {
        fetchedNotifications = await notificationService.getGuestNotifications();
      }
      const readNotifications = JSON.parse(localStorage.getItem('read_notifications') || '[]');
      fetchedNotifications = fetchedNotifications.map(n => ({ ...n, read: readNotifications.includes(n.id) }));
      setNotifications(fetchedNotifications);
      onNotificationsChange?.();
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast({ title: 'Error', description: 'Failed to load notifications', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      await notificationService.markNotificationAsRead(notification.id);
      if (notification.action_url) navigate(notification.action_url);
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-[80] flex items-start justify-end p-4 transition-opacity duration-200",
        isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      )}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className={cn(
          "relative w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden transform transition-transform duration-300",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-white" />
            <h2 className="text-white text-base">Notifications</h2>
          </div>
          <div className="flex items-center gap-2">
            {user && notifications.some(n => !n.read) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  await notificationService.markAllNotificationsAsRead(user.id, isAdmin ? 'admin' : 'user');
                  await loadNotifications();
                }}
                className="text-white bg-white/10 hover:bg-white/20 border-white/20"
              >
                Mark all read
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white/70 hover:text-white">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <ScrollArea className="max-h-[70vh]">
          {loading ? (
            <div className="p-6 text-white/70">Loading...</div>
          ) : notifications.filter(n => !n.read).length === 0 ? (
            <div className="p-6 text-white/70">No new notifications</div>
          ) : (
            <div className="divide-y divide-white/10">
              {notifications.filter(n => !n.read).map((n) => (
                <div key={n.id} onClick={() => handleNotificationClick(n)} className={`p-4 cursor-pointer transition-colors hover:bg-white/5 border-l-4 ${getPriorityColor(n.priority)}`}>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">{getNotificationIcon(n.type, n.icon)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-semibold text-white">{n.title}</p>
                        <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0" />
                      </div>
                      <p className="text-sm text-white/70 mb-2 line-clamp-2">{n.message}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-white/50">{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</p>
                        <Badge variant="outline" className="text-xs text-white border-white/30">{n.type}</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Social section placeholder */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-2 text-white mb-2"><Users className="h-4 w-4" /> Social</div>
          <div className="text-sm text-white/60">Friend requests and chat updates will also appear here.</div>
        </div>
      </div>
    </div>
  );
}
